import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { RefreshCw, ExternalLink, FileText, User, Phone, Mail, MapPin, GraduationCap, Briefcase, AlertCircle, Search } from 'lucide-react';

export default function AdminTeacherDetailsSidebar() {
  const [records, setRecords] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedEmail, setExpandedEmail] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/teacher-sheet');
      setRecords(res.data.records || []);
      setLastSync(res.data.last_sync);
    } catch {
      toast.error('Failed to load teacher details');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/admin/teacher-sheet/refresh');
      toast.success(`Synced ${res.data.synced} teachers from Google Sheet`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = records.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.legal_name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.preferred_name || '').toLowerCase().includes(q) ||
      (r.subjects || '').toLowerCase().includes(q)
    );
  });

  const lastSyncStr = lastSync
    ? new Date(lastSync).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : 'Never';

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Teacher Details</h1>
          <p className="text-xs text-slate-500 mt-1">
            Auto-synced from Google Sheet every 6 hours · Last synced: <strong>{lastSyncStr}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`https://docs.google.com/spreadsheets/d/1y_2wPrYe0h1G6Oe85vF4MwO8cM2Om42g8oOJPJ4RHbs/edit?gid=173003142`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open Sheet
          </a>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="teacher-sheet-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Syncing…' : 'Update Now'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or subject…"
          className="pl-9"
          data-testid="teacher-sheet-search"
        />
      </div>

      {loading && records.length === 0 && (
        <div className="text-slate-500 text-sm">Loading teacher details…</div>
      )}

      {!loading && records.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div>
            No data yet. Click <strong>Update Now</strong> to fetch from the Google Sheet.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((rec) => {
          const isOpen = expandedEmail === rec.email;
          return (
            <div
              key={rec.email}
              className={`bg-white rounded-2xl border shadow-sm p-4 transition ${isOpen ? 'ring-2 ring-blue-200' : 'hover:shadow-md'}`}
              data-testid={`teacher-detail-${rec.email}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{rec.preferred_name || rec.legal_name || '—'}</div>
                    {rec.legal_name && rec.preferred_name && rec.preferred_name !== rec.legal_name && (
                      <div className="text-[11px] text-slate-400 truncate">Legal: {rec.legal_name}</div>
                    )}
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5 truncate">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{rec.email}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {rec.mapped ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                      {rec.teacher_id || 'MAPPED'}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
                      NOT REGISTERED
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                {rec.mobile && (
                  <div className="flex items-center gap-1 text-slate-600 truncate">
                    <Phone className="w-3 h-3 text-slate-400" /> {rec.mobile}
                  </div>
                )}
                {rec.experience_years && (
                  <div className="flex items-center gap-1 text-slate-600 truncate">
                    <Briefcase className="w-3 h-3 text-slate-400" /> {rec.experience_years}
                  </div>
                )}
                {rec.education && (
                  <div className="flex items-center gap-1 text-slate-600 truncate col-span-2">
                    <GraduationCap className="w-3 h-3 text-slate-400" /> {rec.education}
                  </div>
                )}
              </div>

              {rec.subjects && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {rec.subjects.split(',').map((s, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium border border-blue-100">
                      {s.trim()}
                    </span>
                  ))}
                </div>
              )}

              <button
                className="mt-3 text-xs text-blue-600 hover:underline"
                onClick={() => setExpandedEmail(isOpen ? null : rec.email)}
                data-testid={`teacher-detail-toggle-${rec.email}`}
              >
                {isOpen ? 'Hide details' : 'View full details'}
              </button>

              {isOpen && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                  {rec.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                      <span className="whitespace-pre-line">{rec.address}</span>
                    </div>
                  )}
                  {rec.date_of_birth && (
                    <div><span className="text-slate-400">DOB:</span> {rec.date_of_birth}</div>
                  )}
                  {(rec.emergency_contact_name || rec.emergency_contact_phone) && (
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Emergency Contact</div>
                      <div>{rec.emergency_contact_name} {rec.emergency_contact_relationship && `(${rec.emergency_contact_relationship})`}</div>
                      <div>{rec.emergency_contact_phone}</div>
                    </div>
                  )}
                  {rec.upi_id && <div><span className="text-slate-400">UPI:</span> {rec.upi_id}</div>}
                  {rec.communication_preference && <div><span className="text-slate-400">Prefers:</span> {rec.communication_preference}</div>}
                  {rec.tech_proficiency && <div><span className="text-slate-400">Tech Proficiency:</span> {rec.tech_proficiency}/10</div>}
                  {rec.graduation_certificate_url && (
                    <a href={rec.graduation_certificate_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                      <FileText className="w-3 h-3" /> Graduation Certificate
                    </a>
                  )}
                  <div className="text-[10px] text-slate-400 pt-1">
                    Submitted: {rec.submitted_at} · Synced: {rec.last_synced_at && new Date(rec.last_synced_at).toLocaleString('en-IN')}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
